import { Link, NavLink, useLocation } from "react-router-dom"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar"
import { navigation, type NavItem } from "@/config/navigation"
import { siteConfig } from "@/config/site"
import { useReviewCount } from "@/hooks/use-ai-generations"
import { NavUser } from "@/components/layout/nav-user"

function isItemActive(item: NavItem, pathname: string) {
  return item.end ? pathname === item.href : pathname === item.href || pathname.startsWith(`${item.href}/`)
}

export function BrandMark({ className = "size-8" }: { className?: string }) {
  return (
    <div className={`flex aspect-square items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm ${className}`}>
      <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M7 4h6.5a3.5 3.5 0 0 1 0 7H7z" />
        <path d="M7 11h7.5a4 4 0 0 1 0 8H7z" />
      </svg>
    </div>
  )
}

export function AppSidebar() {
  const { pathname } = useLocation()
  const { isMobile, setOpenMobile } = useSidebar()
  const { count: reviewCount } = useReviewCount()

  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  const badges: Record<string, number> = {
    "/dashboard/ai-generations": reviewCount,
  }

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild tooltip={siteConfig.product}>
              <Link to="/dashboard" onClick={closeOnMobile}>
                <BrandMark />
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-semibold text-sidebar-accent-foreground">{siteConfig.name}</span>
                  <span className="truncate text-xs text-sidebar-foreground/70">Admin console</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navigation.map((group) => (
          <SidebarGroup key={group.label} className="py-1">
            <SidebarGroupLabel className="text-eyebrow text-sidebar-foreground/80">{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const badge = badges[item.href]
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={isItemActive(item, pathname)} tooltip={item.title}>
                      <NavLink to={item.href} end={item.end} onClick={closeOnMobile}>
                        <item.icon />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                    {badge ? (
                      <SidebarMenuBadge className="rounded-full border bg-secondary text-ink-2 tabular-nums">
                        {badge > 99 ? "99+" : badge}
                      </SidebarMenuBadge>
                    ) : null}
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
